import { useCallback, useEffect, useRef, useState } from 'react'

// Native TTS: @capacitor-community/text-to-speech@6.1.0 (Capacitor 7 wali line)
// Browser/TV: Web Speech API

// Indian accent ke liye preference order
const PREFERRED_LANGS = ['en-IN', 'hi-IN']

// plugin ki QueueStrategy: 0 = Flush (chalti awaaz kaat do), 1 = Add (queue me lagao)
const QUEUE_FLUSH = 0
const QUEUE_ADD = 1

/**
 * Patient ka naam bolne ke liye TTS hook.
 * Android WebView me speechSynthesis reliable nahi hai, isliye native plugin pehle try hota hai.
 *
 * return: { speak, primeSpeech, stop, ready, supported }
 *  - supported: current lang device par available hai ya nahi (hi-IN voice data missing ho sakta hai)
 */
export default function useSpeak({ lang = 'en-IN', rate = 0.9, pitch = 1, volume = 1 } = {}) {
  const [ready, setReady] = useState(false)
  const [supported, setSupported] = useState(true)
  const voiceRef = useRef(null)
  const nativeRef = useRef(null)

  // ---- native (Capacitor) TTS ----
  useEffect(() => {
    let alive = true
    const isNative = !!(window.Capacitor?.isNativePlatform?.())
    if (!isNative) return
    import('@capacitor-community/text-to-speech')
      .then((m) => {
        if (!alive) return
        nativeRef.current = m.TextToSpeech
        setReady(true)
      })
      .catch(() => { /* plugin install nahi hai -> browser fallback */ })
    return () => { alive = false }
  }, [])

  // ---- browser voices (async load hoti hain) ----
  useEffect(() => {
    if (!('speechSynthesis' in window)) return
    const norm = (v) => (v.lang || '').replace('_', '-')

    const pickVoice = () => {
      const voices = window.speechSynthesis.getVoices() || []
      if (!voices.length) return
      const exact = voices.find((v) => norm(v) === lang)
      voiceRef.current =
        exact ||
        PREFERRED_LANGS.map((l) => voices.find((v) => norm(v) === l)).find(Boolean) ||
        voices.find((v) => norm(v).startsWith('en')) ||
        null
      if (!nativeRef.current) setSupported(!!exact)
      setReady(true)
    }

    pickVoice()
    window.speechSynthesis.onvoiceschanged = pickVoice
    return () => { window.speechSynthesis.onvoiceschanged = null }
  }, [lang])

  // ---- native: lang device par installed hai ya nahi ----
  useEffect(() => {
    let alive = true
    const check = async () => {
      if (!nativeRef.current) return
      try {
        const res = await nativeRef.current.isLanguageSupported({ lang })
        if (alive) setSupported(!!res?.supported)
      } catch (e) { if (alive) setSupported(true) }
    }
    check()
    return () => { alive = false }
  }, [lang, ready])

  const speak = useCallback(async (text, { interrupt = true } = {}) => {
    if (!text) return
    try {
      if (nativeRef.current) {
        await nativeRef.current.speak({
          text,
          lang,
          rate,
          pitch,
          volume,
          category: 'ambient',
          queueStrategy: interrupt ? QUEUE_FLUSH : QUEUE_ADD,
        })
        return
      }
      if (!('speechSynthesis' in window)) return
      if (interrupt) window.speechSynthesis.cancel()
      const u = new SpeechSynthesisUtterance(text)
      if (voiceRef.current) u.voice = voiceRef.current
      u.lang = voiceRef.current?.lang || lang
      u.rate = rate
      u.pitch = pitch
      u.volume = volume
      window.speechSynthesis.speak(u)
    } catch (e) {
      console.warn('tts error', e)
    }
  }, [lang, rate, pitch, volume])

  // pehle tap par silent utterance -> browser TTS unlock ho jata hai (native me zaroorat nahi)
  const primeSpeech = useCallback(() => {
    if (nativeRef.current) return
    if (!('speechSynthesis' in window)) return
    try {
      const u = new SpeechSynthesisUtterance(' ')
      u.volume = 0
      window.speechSynthesis.speak(u)
    } catch (e) { /* ignore */ }
  }, [])

  const stop = useCallback(() => {
    try { window.speechSynthesis?.cancel() } catch (e) { /* ignore */ }
    try { nativeRef.current?.stop() } catch (e) { /* ignore */ }
  }, [])

  // device par voice data missing ho to Android ka install screen khol dein (sirf native)
  const openVoiceInstall = useCallback(async () => {
    try { await nativeRef.current?.openInstall() } catch (e) { /* ignore */ }
  }, [])

  useEffect(() => () => { stop() }, [stop])

  return { speak, primeSpeech, stop, openVoiceInstall, ready, supported }
}

/**
 * Announcement text banata hai.
 * spellNumber: "5 4" (digit by digit) — bheed me "fifty four" se zyada clear sunai deta hai.
 */
export const buildAnnouncement = (data, kind = 'cabin', { hindi = false, spellNumber = true } = {}) => {
  if (!data) return ''
  const name = (data.patientName || '').trim()
  const raw = String(data.confirmedAllotedNumber ?? '')
  const num = spellNumber ? raw.split('').join(' ') : raw

  if (hindi) {
    return kind === 'cabin'
      ? `नंबर ${num}। ${name}। कृपया डॉक्टर के केबिन में आइए।`
      : `नंबर ${num}। ${name}। कृपया तैयार रहें, आपका नंबर अगला है।`
  }
  return kind === 'cabin'
    ? `Number ${num}. ${name}. ${name}. Please come to the doctor's cabin.`
    : `Number ${num}. ${name}. Please get ready, you are next.`
}